/**
 * The pieces a marketing page is built from.
 *
 * One rule governs the whole file: every number carries where it came from.
 * This product's promise is that it does not invent things about a business.
 * A landing page that rounds a statistic up because it reads better has
 * broken that promise before anybody has signed up.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { ArrowRight, Check, ChevronLeft, ChevronRight, Dot, Globe } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useArrived, useInView, usePrefersReducedMotion } from './media'
import { Proof, Status } from './primitives'
import { aiAgents } from './botmarks'
import './market.css'

/* ---------------- A number worth putting on a page ---------------- */

export function Stats({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('ds-bignums', className)}>{children}</div>
}

/**
 * The source is not a footnote and not a tooltip: it sits in the block, in
 * the reader's line of sight. The prop is required, so a number cannot reach
 * a page without one.
 */
export function Stat({
  num,
  said,
  more,
  from,
}: {
  num: string
  said: string
  more?: string
  from: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  const shown = useCountUp(num, inView && !still)

  return (
    <div className="ds-bignum" ref={ref}>
      {/*
        The figure is announced once, whole, so a screen reader is read the
        number rather than every frame on the way to it.
      */}
      <div className="ds-bignum__num" aria-hidden="true">{shown}</div>
      <span className="ds-sr">{num}</span>
      <p className="ds-bignum__said">{said}</p>
      {more && <p className="ds-bignum__more">{more}</p>}
      <div className="ds-bignum__from">{from}</div>
    </div>
  )
}

/*
  Counts a figure up to what it says, once, when it comes into view.

  This is the one place the page moves on its own, and it is here because
  Kevin asked for it. The figure keeps its own shape: "51%" counts in whole
  numbers, "4.4x" counts to one decimal, and whatever sits around the digits
  is carried through untouched rather than reassembled from a suffix prop.

  It never animates under prefers-reduced-motion, it never runs twice, and
  the real value is in the DOM for anything that is not watching.
*/
function useCountUp(value: string, run: boolean) {
  const [shown, setShown] = useState(value)
  const done = useRef(false)

  /* "51%" -> ["51%", "", "51", "%"]. Whatever sits around the digits is
     carried through rather than reassembled from a suffix prop.

     A figure with a second number in it is a range, not a figure, and a
     range cannot count: "5-10%" matched here as five with "-10%" hanging off
     it, so the reader watched a statistic that read "0-10%" climb to
     "5-10%". A range is shown as it is written. */
  const matched = value.match(/^(\D*)(\d[\d,]*(?:\.\d+)?)(.*)$/)
  const parts = matched && /\d/.test(matched[3]) ? null : matched

  /* A thousands separator is not a second number. "3,000+" used to match as
     three with ",000+" hanging off it, which tripped the range guard above
     and left the figure sitting still while its neighbours counted. The
     digits are read without their commas and written back with them. */
  const group = (n: number, places: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places })

  /*
    Start the figure at zero before the first paint, but only when it is
    actually going to count. The observer fires at a quarter visible, which
    means the real number has already been painted by then: without this the
    reader watches it appear, drop to zero, and climb back, which is worse
    than not animating at all. matchMedia is read here rather than through
    the hook because the hook is false on the first render and corrected
    after it, and one frame is all this needs to be wrong.
  */
  useLayoutEffect(() => {
    if (!parts || typeof window === 'undefined') return
    if (typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const [, before, digits, after] = parts
    const places = digits.includes('.') ? digits.split('.')[1].length : 0
    setShown(before + group(0, places) + after)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (!run || done.current || !parts) return

    const [, before, digits, after] = parts
    const target = Number(digits.replace(/,/g, ''))
    if (!Number.isFinite(target)) return
    const places = digits.includes('.') ? digits.split('.')[1].length : 0

    done.current = true
    const started = performance.now()
    const DURATION = 900
    let frame = 0

    const step = (now: number) => {
      const t = Math.min(1, (now - started) / DURATION)
      /* Eased out, so it arrives rather than stops. */
      const eased = 1 - Math.pow(1 - t, 3)
      setShown(before + group(Number((target * eased).toFixed(places)), places) + after)
      if (t < 1) frame = requestAnimationFrame(step)
      else setShown(value)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, value])

  return shown
}

/* ---------------- What changed ---------------- */

export function Shift({
  was,
  now,
}: {
  was: { when: string; what: string; points: string[] }
  now: { when: string; what: string; points: string[] }
}) {
  return (
    <div className="ds-shift">
      <div className="ds-shift__half ds-shift__half--was">
        <div className="ds-shift__when">{was.when}</div>
        <p className="ds-shift__what">{was.what}</p>
        <ul className="ds-shift__list">
          {was.points.map((p) => (
            <li key={p}>
              <Dot aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
      </div>
      <div className="ds-shift__half">
        <div className="ds-shift__when">{now.when}</div>
        <p className="ds-shift__what">{now.what}</p>
        <ul className="ds-shift__list">
          {now.points.map((p) => (
            <li key={p}>
              <Dot aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* ---------------- One thing at a time ---------------- */

export function Showcase({
  what,
  more,
  foot,
  shot,
  flip,
}: {
  what: string
  more: string
  foot?: ReactNode
  shot: ReactNode
  flip?: boolean
}) {
  return (
    <div className={cn('ds-showcase', flip && 'ds-showcase--flip')}>
      <div className="ds-showcase__words">
        <h3 className="ds-showcase__what">{what}</h3>
        <p className="ds-showcase__more">{more}</p>
        {foot && <div className="ds-showcase__foot">{foot}</div>}
      </div>
      <div className="ds-showcase__shot">{shot}</div>
    </div>
  )
}

/* ---------------- Who it is for ---------------- */

export function ForWho({ children }: { children: ReactNode }) {
  return <div className="ds-forwho">{children}</div>
}

export function Who({
  who,
  sounds,
  gets,
  foot,
}: {
  who: string
  sounds: string
  gets: string[]
  foot?: ReactNode
}) {
  return (
    <div className="ds-forwho__one">
      <div className="ds-forwho__who">{who}</div>
      <p className="ds-forwho__sounds">{sounds}</p>
      <ul className="ds-forwho__list">
        {gets.map((g) => (
          <li key={g}>
            <Check aria-hidden="true" />
            {g}
          </li>
        ))}
      </ul>
      {foot && <div className="ds-forwho__foot">{foot}</div>}
    </div>
  )
}

/* ---------------- What you can buy ---------------- */

export function Offer({
  name,
  forWhom,
  price,
  terms,
  includes,
  foot,
}: {
  name: string
  forWhom: string
  price: string
  terms: string
  includes: string[]
  foot?: ReactNode
}) {
  return (
    <div className="ds-offer">
      <div className="ds-offer__name">{name}</div>
      <p className="ds-offer__for">{forWhom}</p>
      <div className="ds-offer__price">{price}</div>
      <div className="ds-offer__terms">{terms}</div>
      <ul className="ds-offer__list">
        {includes.map((i) => (
          <li key={i}>
            <Check aria-hidden="true" />
            {i}
          </li>
        ))}
      </ul>
      {foot && <div className="ds-offer__foot">{foot}</div>}
    </div>
  )
}

/* ---------------- What is in it ---------------- */

export function Course({
  lessons,
  walk,
  ends,
}: {
  lessons: { title: string; line: string; long: string }[]
  /**
   * Draws a rule down the steps and lights them in order, once, when the
   * list arrives. Opt-in, because a list of what is in something is not
   * always a sequence, and a rule down the side says that it is.
   *
   * The marker is ink, not a confirmed tick: these are the steps in an
   * order, not work already done for the reader.
   */
  walk?: boolean
  /**
   * What the steps are for.
   *
   * A numbered list of three things tells a reader what happens and never
   * says why any of it is worth doing. This is the last row and it is not a
   * step: it carries no number, the rule runs into it rather than past it,
   * and it is the one row drawn in the confirmed blue, because the end of
   * this sequence is the state where what AI can find out about a business
   * is right.
   *
   * It is a claim, so it says only what this product actually controls: what
   * an assistant can reach and read, and whether it stays that way. Never
   * what an assistant will then say, and never a number.
   */
  ends?: { title: string; line: string }
}) {
  const [ref, arrived] = useArrived<HTMLOListElement>()
  return (
    <ol
      className={cn('ds-course', walk && 'ds-course--walk')}
      ref={walk ? ref : undefined}
      data-arrived={walk && arrived ? 'true' : undefined}
    >
      {lessons.map((l, i) => (
        <li key={l.title} className="ds-course__row" style={{ '--ds-step-i': i } as CSSProperties}>
          {walk && <span className="ds-course__dot" aria-hidden="true" />}
          <span className="ds-course__n">{i + 1}</span>
          <div className="ds-course__body">
            <div className="ds-course__title">{l.title}</div>
            <p className="ds-course__line">{l.line}</p>
          </div>
          <span className="ds-course__long">{l.long}</span>
        </li>
      ))}
      {ends && (
        <li
          className="ds-course__row ds-course__row--end"
          style={{ '--ds-step-i': lessons.length } as CSSProperties}
        >
          {walk && (
            <span className="ds-course__dot ds-course__dot--end" aria-hidden="true">
              <Check />
            </span>
          )}
          <div className="ds-course__end">
            <h3 className="ds-course__end-title">{ends.title}</h3>
            <p className="ds-course__end-line">{ends.line}</p>
          </div>
        </li>
      )}
    </ol>
  )
}

/* ---------------- How this is different ---------------- */

/**
 * By approach, never by name. Naming a rival means making claims about their
 * product that we cannot show and they will dispute, and it puts their name
 * on our page for free.
 */
export function Versus({
  ways,
}: {
  ways: { way: string; what: string; more: string; ours?: boolean }[]
}) {
  return (
    <div className="ds-versus">
      {ways.map((w) => (
        <div key={w.way} className={cn('ds-versus__one', w.ours && 'ds-versus__one--ours')}>
          <div className="ds-versus__way">{w.way}</div>
          <p className="ds-versus__what">{w.what}</p>
          <p className="ds-versus__more">{w.more}</p>
        </div>
      ))}
    </div>
  )
}

/* ---------------- What customers said ---------------- */

/*
  A rail of short reviews, which is a different register from `Story`. Story
  is one customer at length beside their photograph, and it falls back to the
  honest slot when the photograph does not exist; five honest slots in a row
  would read as a broken page rather than as an asset still to come. These are
  words people actually wrote, carried without a picture, without a star
  rating, and without a headline we composed for them.

  No rating, deliberately. The product has published invented ones before.

  It runs off the right edge of the viewport rather than wrapping to a second
  row, because a 3-then-2 grid leaves a hole where the fourth and fifth cards
  do not fill their row, and because the overflow is what tells a reader there
  is more. The page never moves on its own: this moves only when a reader
  drags it, flicks it, or presses one of the two controls. That is the
  difference between this and the logo strip that used to slide past by
  itself.
*/
export function Says({ title, children }: { title: string; children: ReactNode }) {
  const rail = useRef<HTMLDivElement>(null)
  const still = usePrefersReducedMotion()
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(true)

  /* Both ends true means it does not overflow, so the controls are not
     offered at all rather than offered dead. */
  const read = useCallback(() => {
    const el = rail.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setAtStart(el.scrollLeft <= 1)
    setAtEnd(el.scrollLeft >= max - 1)
  }, [])

  useEffect(() => {
    const el = rail.current
    if (!el) return
    read()
    el.addEventListener('scroll', read, { passive: true })
    /* Cards reflow with the container, not with the window: a resize
       listener alone misses the sidebar opening and the font loading. */
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read)
    observer?.observe(el)
    return () => {
      el.removeEventListener('scroll', read)
      observer?.disconnect()
    }
  }, [read])

  /* One card per press, measured off a real card so it stays right when the
     card width changes with the viewport. */
  const step = (dir: 1 | -1) => {
    const el = rail.current
    if (!el) return
    const card = el.firstElementChild as HTMLElement | null
    const by = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8
    el.scrollBy({ left: dir * by, behavior: still ? 'auto' : 'smooth' })
  }

  const fits = atStart && atEnd

  return (
    <div className="ds-says">
      <div className="ds-says__head">
        <h2 className="ds-h1">{title}</h2>
        {!fits && (
          <div className="ds-says__acts">
            <button
              type="button"
              className="ds-says__arrow"
              onClick={() => step(-1)}
              disabled={atStart}
              aria-label="Previous reviews"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ds-says__arrow"
              onClick={() => step(1)}
              disabled={atEnd}
              aria-label="More reviews"
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {/*
        Focusable and labelled, so the rail is reachable with a keyboard even
        though the two controls are the obvious way in. Without it a keyboard
        user can tab to a card that is scrolled out of sight and never see it.
      */}
      <div
        ref={rail}
        className="ds-says__rail"
        tabIndex={0}
        role="group"
        aria-label={title}
        data-more={!atEnd}
      >
        {children}
      </div>
    </div>
  )
}

export function Say({
  head,
  said,
  who,
}: {
  /** The reviewer's own title for their review. */
  head: string
  said: string
  who: string
}) {
  return (
    <figure className="ds-say">
      {/* The mark that says this is somebody talking. Decorative to a screen
          reader, which gets the <blockquote> instead. */}
      <span className="ds-say__mark" aria-hidden="true">&ldquo;</span>
      <blockquote className="ds-say__body">
        <p className="ds-say__head">{head}</p>
        <p className="ds-say__said">{said}</p>
      </blockquote>
      <figcaption className="ds-say__who">{who}</figcaption>
    </figure>
  )
}

/* ---------------- A correction, shown where it happened ---------------- */

/**
 * One page, one fact, and the moment what AI was being told stopped being
 * true. The whole product in a single card, which is why it earns the width
 * a lead cell gives it.
 *
 * The correction is the only thing in a marketing section that moves without
 * a reader asking, and it moves the way the application does: in place, on
 * the row that was wrong, once. Nothing slides in and nothing fades up. The
 * card is whole from the first paint, so under reduced motion the reader
 * arrives at the finished state rather than at an empty frame.
 *
 * The two status chips share one grid cell rather than replacing each other,
 * so the swap cannot change the width of the row it sits in. The pair is
 * hidden from screen readers because it is the same fact the proof line
 * underneath states in words.
 */
export function PutBack({
  page,
  says,
  told,
  when,
  next,
  sample = true,
}: {
  /** The page's address, as a person would read it aloud. */
  page: string
  /** What the page itself says today. */
  says: string
  /** What AI agents were being given instead. */
  told: string
  /** Plain words for when it was put back, e.g. "Put back last night". */
  when: string
  /** What happens next, in plain words. */
  next: string
  /** Marks the page as fictional. Required anywhere it is not a real customer's. */
  sample?: boolean
}) {
  const [ref, arrived] = useArrived<HTMLDivElement>()
  return (
    <div className="ds-putback" ref={ref} data-arrived={arrived || undefined}>
      <div className="ds-putback__page">
        <Globe aria-hidden="true" />
        <span className="ds-putback__url">{page}</span>
        {sample && <span className="ds-putback__sample">sample page</span>}
      </div>
      <div className="ds-putback__rows">
        <div className="ds-putback__row">
          <span className="ds-putback__label">Your page says</span>
          <span className="ds-putback__value">{says}</span>
        </div>
        <div className="ds-putback__row">
          <span className="ds-putback__label">AI agents were being told</span>
          <span className="ds-putback__value">
            {/*
              The value twice: the plain line, and a struck copy laid over it
              that a clip opens from the left. Drawing a rule across the line
              with one pseudo-element only ever struck the first line box, so
              a value that wrapped was half corrected. Two layers wrap the
              same way, so the strike follows the text wherever it goes. The
              copy on top is decoration and stays out of the tree.
            */}
            <span className="ds-putback__was">
              <span className="ds-putback__struck" aria-hidden="true">
                {told}
              </span>
              {told}
            </span>
          </span>
          <span className="ds-putback__state" aria-hidden="true">
            <Status kind="look" size="sm" className="ds-putback__before" />
            <Status kind="good" size="sm" className="ds-putback__after" />
          </span>
        </div>
      </div>
      <div className="ds-putback__foot">
        <Proof title={when} detail={next} />
      </div>
    </div>
  )
}

/* ---------------- A demonstration: the exchange, acted out ---------------- */

/**
 * One live probe: what we asked an assistant, what it answered, and what
 * that means. The page beside it claims we run probes and compare the
 * answers; this acts that out rather than describing it.
 *
 * It is a demonstration, not a showreel. It runs once when it comes into
 * view, holds the result, and carries no play control, which is why three of
 * them can sit side by side: each one stops. A reader who points at it gets
 * the finished state immediately, and so does anybody who has asked for
 * reduced motion.
 *
 * Nothing here is revealed by appearing. Every line is in the document at
 * full size from the first paint and is uncovered by a clip, so the card
 * never changes height and a screen reader is read the whole exchange rather
 * than one frame of it.
 */
export function Probe({
  ask,
  said,
  verdict,
  delay = 0,
}: {
  /** The question, in a person's words. Short enough to sit on one line. */
  ask: string
  /** The assistant's answer, quoted. Somebody else's words, so it is set apart. */
  said: string
  /** What the answer means, in the words a status chip would use. */
  verdict: string
  /** Milliseconds before this one starts, so a row of them does not all move at once. */
  delay?: number
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  // 0 nothing yet, 1 asked, 2 answered, 3 the verdict.
  const [step, setStep] = useState(still ? 3 : 0)
  const started = useRef(still)
  const timers = useRef<number[]>([])

  const stop = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  // The Attention Rule: the first hover, focus or tap ends it for good.
  const handOver = useCallback(() => {
    started.current = true
    stop()
    setStep(3)
  }, [stop])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    timers.current = [
      window.setTimeout(() => setStep(1), delay),
      window.setTimeout(() => setStep(2), delay + 1150),
      window.setTimeout(() => setStep(3), delay + 2060),
    ]
    /*
      No cleanup on `inView`. This effect used to return `stop`, so React
      cleared the pending timers the moment the card left the viewport:
      scrolling past quickly froze it half-drawn, and `started` was already
      true so coming back never restarted it. It ran to completion only if
      you kept it on screen. Once begun it now finishes wherever the page
      is, and the unmount effect below is what clears the timers.
    */
  }, [inView, delay, stop])

  useEffect(() => stop, [stop])

  return (
    <div
      className="ds-probe"
      ref={ref}
      data-asked={step >= 1 ? 'true' : undefined}
      data-said={step >= 2 ? 'true' : undefined}
      data-verdict={step >= 3 ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <p className="ds-small ds-faint">We asked</p>
      <p className="ds-probe__ask">
        <span className="ds-probe__wipe">{ask}</span>
        <i className="ds-probe__caret" aria-hidden="true" />
      </p>
      <p className="ds-small ds-faint" style={{ marginTop: 12 }}>
        It answered
      </p>
      <blockquote className="ds-probe__said">{said}</blockquote>
      <div className="ds-probe__verdict">
        <Status kind="look" size="sm">
          {verdict}
        </Status>
      </div>
    </div>
  )
}

/**
 * A buyer asks an AI who to use, and the answer names you.
 *
 * This is the outcome the product is sold on, drawn rather than described:
 * the question is the one a buyer actually types, and the chart under it is
 * who the answer comes back with. It is the homepage's opening
 * demonstration and it sits on the hero photograph, where a person is
 * scrolling past rather than studying, so it is a picture with a handful of
 * words on it and nothing to work through.
 *
 * It carries no numbers, and that is deliberate rather than an omission.
 * The bars are relative lengths and the question names a category and not a
 * company. A recommendation rate on this card ("named in 7 of 10 answers")
 * would be an invented statistic about an outcome, which is the exact shape
 * of the 162 fabricated ratings this product shipped once already. What the
 * picture says is that you are on the list and at the top of it; what it
 * does not say is how often, to whom, or by how much.
 *
 * It used to also carry the word "example". That word was doing real work
 * while there were figures on the card, and there are none: no rate, no
 * share, no company, no named rivals. What is left is a second-person
 * drawing with an unlabelled spread of bars, and the assistant's mark now
 * says whose answer it is. Nothing on it can be read as a measurement, so
 * nothing on it needs to be excused as one.
 *
 * The competitors are unnamed bars. Inventing four rival brands to lose to
 * you is a claim about a market rather than a picture of an outcome, and
 * the reference this was drawn from does exactly that.
 *
 * Uncovered, not added: every part is in the document at full size from the
 * first paint. The bars grow by `scaleX` from their left edge and the words
 * are revealed by a clip, so nothing changes the card's height and a screen
 * reader has the whole thing from the first frame.
 *
 * It runs once and stops. A showreel loops and carries a play control, and
 * only one of those may be on a screen at a time; the Inspector below this
 * hero is that one.
 *
 */
export function Named({
  ask,
  you,
  others,
  note,
  className,
}: {
  /** The buyer's question. A category, never a company: naming one in the
   *  lead line asks a reader to know who that is before they can read the
   *  picture. */
  ask: string
  /**
   * The name on the top bar. It is the reader, so it is "You".
   *
   * `mark` and `label` used to sit here: one assistant's logo, and a line
   * of text reading "Who ChatGPT names". Both are gone. The card is about
   * being recommended by AI, not by one vendor, so it draws all four marks
   * from `aiAgents` itself and writes its own sentence around them. That
   * also removes the way the two could disagree, which they did: a caller
   * could pass Gemini's mark beside the words "Who ChatGPT names".
   */
  you: string
  /** The other bars, as relative lengths from 0 to 100. Unnamed. */
  others: number[]
  /** One line saying why the answer came out this way. */
  note: string
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  /* The sentence a screen reader gets, built from the same list the marks
     are, so the two cannot drift. */
  const srLabel = `Who ${aiAgents.map((a) => a.name).join(', ')} recommends`
  /* Step 0 is nothing, 1 is the question, then the other bars grow, then
     yours, then the note. Reduced motion starts at the end. */
  const last = others.length + 3
  const [step, setStep] = useState(still ? last : 0)
  const started = useRef(still)
  const timers = useRef<number[]>([])

  const stop = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  // The Attention Rule: the first hover, focus or tap ends it for good.
  const handOver = useCallback(() => {
    started.current = true
    stop()
    setStep(last)
  }, [stop, last])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    /* The others go up quickly and close together, then a beat, then yours.
       The pause before the last bar is what makes the outcome read as the
       point rather than as the fourth item in a list. */
    const at = [420, ...others.map((_, i) => 900 + i * 220)]
    at.push(900 + others.length * 220 + 420)
    at.push(900 + others.length * 220 + 1100)
    timers.current = at.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms))
    /*
      No cleanup on `inView`. This effect used to return `stop`, so React
      cleared the pending timers the moment the card left the viewport:
      scrolling past quickly froze it half-drawn, and `started` was already
      true so coming back never restarted it. It ran to completion only if
      you kept it on screen. Once begun it now finishes wherever the page
      is, and the unmount effect below is what clears the timers.
    */
  }, [inView, others, stop])

  useEffect(() => stop, [stop])

  const yoursAt = others.length + 2

  return (
    <div
      className={cn('ds-named', className)}
      ref={ref}
      data-asked={step >= 1 ? 'true' : undefined}
      data-done={step >= last ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <div className="ds-named__ask">
        <p className="ds-named__q">
          <span className="ds-named__wipe">{ask}</span>
        </p>
      </div>

      <div className="ds-named__answer">
        {/*
          Who is being asked, drawn rather than written. This said "Who
          ChatGPT names" beside a single mark, which named one assistant on
          a card about being recommended by all of them. The marks are the
          four in `aiAgents`, so the row cannot fall out of step with the
          set the rest of the site tracks.

          The sentence is read out whole for a screen reader and the marks
          are hidden from it: four logos announced one at a time between
          "Who" and "recommends" is not a sentence.
        */}
        <p className="ds-small ds-faint ds-named__label">
          <span className="ds-sr">{srLabel}</span>
          <span aria-hidden="true" className="ds-named__asked">
            Who
            <span className="ds-named__marks">
              {aiAgents.map(({ name, Logo }) => (
                <span key={name} className="ds-named__mark">
                  <Logo ariaLabel="" />
                </span>
              ))}
            </span>
            recommends&hellip;
          </span>
        </p>
        <div className="ds-named__chart">
          <div
            className="ds-named__row ds-named__row--you"
            data-on={step >= yoursAt ? 'true' : undefined}
          >
            <span className="ds-named__bar" style={{ '--ds-named-len': '100%' } as CSSProperties} />
            <span className="ds-named__who">{you}</span>
          </div>
          {/*
            The other bars stay unnamed: inventing four rivals to lose to
            you is a claim about a market rather than a picture of an
            outcome. One of them says what they are, once, because a reader
            who does not already know what the grey bars mean has nothing
            on the card to tell them.
          */}
          {others.map((len, i) => (
            <div
              key={`${len}-${i}`}
              className="ds-named__row"
              data-on={step >= 2 + i ? 'true' : undefined}
            >
              <span className="ds-named__bar" style={{ '--ds-named-len': `${len}%` } as CSSProperties} />
              {i === 0 && <span className="ds-named__rest">Your competition</span>}
            </div>
          ))}
        </div>
        <p className="ds-small ds-named__note">{note}</p>
      </div>
    </div>
  )
}

/**
 * A website being scanned, and the checks that come back.
 *
 * The methodology page's opening demonstration, laid on its hero
 * photograph. It acts out the first two sentences of the page: a site is
 * read, and five named things about it are checked.
 *
 * The five are the page's own five, by name, so the picture and the section
 * underneath it cannot drift apart. They are single words because they are
 * read at a glance beside a headline, not studied.
 *
 * The sweep passes down the page once and stops. It is a demonstration
 * acting out the claim beside it, which is one of the five motions this
 * system allows, and it is bounded the same way every other one is: it
 * plays in view, it runs once, and the first hover, focus or tap hands it
 * over permanently.
 *
 * No score, and no count. A figure on this card would be an invented
 * measurement of a website that does not exist, and the page it sits on is
 * the one that says what the product refuses to claim.
 *
 * Uncovered, not added: the rows are all in the document at full size from
 * the first paint and the ticks are revealed rather than inserted, so the
 * card never changes height and a screen reader has the whole list from the
 * first frame.
 */
export function Checked({
  site,
  checks,
  note,
  className,
}: {
  /** The site being read. A placeholder domain, never a real customer's. */
  site: string
  /** What is checked, one word each. The page's own five. */
  checks: string[]
  /** One line under the list. */
  note?: string
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  /* Step 0 is nothing, 1 starts the sweep, then one step per check, then
     the note. Reduced motion starts at the end. */
  const last = checks.length + 2
  const [step, setStep] = useState(still ? last : 0)
  const started = useRef(still)
  const timers = useRef<number[]>([])

  const stop = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  // The Attention Rule: the first hover, focus or tap ends it for good.
  const handOver = useCallback(() => {
    started.current = true
    stop()
    setStep(last)
  }, [stop, last])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    /* The sweep runs first and the checks land behind it, one after another,
       so the reading and the result read as cause and effect rather than as
       two things happening at once. */
    const at = [260, ...checks.map((_, i) => 1180 + i * 300)]
    at.push(1180 + checks.length * 300 + 220)
    timers.current = at.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms))
    /*
      No cleanup on `inView`. This effect used to return `stop`, so React
      cleared the pending timers the moment the card left the viewport:
      scrolling past quickly froze it half-drawn, and `started` was already
      true so coming back never restarted it. It ran to completion only if
      you kept it on screen. Once begun it now finishes wherever the page
      is, and the unmount effect below is what clears the timers.
    */
  }, [inView, checks, stop])

  useEffect(() => stop, [stop])

  return (
    <div
      className={cn('ds-checked', className)}
      ref={ref}
      data-reading={step >= 1 ? 'true' : undefined}
      data-done={step >= last ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <div className="ds-checked__page">
        <p className="ds-checked__site">{site}</p>
        {/* The page's own content, drawn rather than written: this is a
            picture of a website being read, and words on it would be words
            about a business that does not exist. */}
        <span className="ds-checked__lines" aria-hidden="true">
          <i style={{ width: '86%' }} />
          <i style={{ width: '64%' }} />
          <i style={{ width: '92%' }} />
          <i style={{ width: '48%' }} />
        </span>
        <span className="ds-checked__sweep" aria-hidden="true" />
      </div>

      <div className="ds-checked__result">
        <ul className="ds-checked__list">
          {checks.map((check, i) => (
            <li key={check} className="ds-checked__row" data-on={step >= 2 + i ? 'true' : undefined}>
              <span className="ds-checked__tick" aria-hidden="true">
                <Check />
              </span>
              <span className="ds-checked__what">{check}</span>
            </li>
          ))}
        </ul>
        {note && <p className="ds-small ds-checked__note">{note}</p>}
      </div>
    </div>
  )
}

/**
 * A sequence of things arriving, each with its own mark.
 *
 * The general case behind three of the product demonstrations: crawlers
 * turning up on a page, the steps of a deployment completing, the formats a
 * registry listing is served in. What they share is that a set of named
 * things land one after another and then stop.
 *
 * No counts on it. "GPTBot, 412 visits" would be an invented measurement of
 * a website that does not exist, and these sit on marketing pages.
 *
 * Uncovered, not added: every row is in the document at full size from the
 * first paint and each one's mark keeps its space whether or not it has
 * landed, so the card never changes height.
 */
export function Lands({
  label,
  rows,
  note,
  className,
}: {
  /** What the list is, in three or four words. */
  label: string
  /** Each row: a mark, and a short line. */
  rows: { mark: ReactNode; said: string }[]
  /** One line underneath, once they have all landed. */
  note?: string
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  const last = rows.length + 1
  const [step, setStep] = useState(still ? last : 0)
  const started = useRef(still)
  const timers = useRef<number[]>([])

  const stop = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  const handOver = useCallback(() => {
    started.current = true
    stop()
    setStep(last)
  }, [stop, last])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    const at = [...rows.map((_, i) => 380 + i * 520), 380 + rows.length * 520 + 180]
    timers.current = at.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms))
    /*
      No cleanup on `inView`. This effect used to return `stop`, so React
      cleared the pending timers the moment the card left the viewport:
      scrolling past quickly froze it half-drawn, and `started` was already
      true so coming back never restarted it. It ran to completion only if
      you kept it on screen. Once begun it now finishes wherever the page
      is, and the unmount effect below is what clears the timers.
    */
  }, [inView, rows, stop])

  useEffect(() => stop, [stop])

  return (
    <div
      className={cn('ds-lands', className)}
      ref={ref}
      data-done={step >= last ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <p className="ds-small ds-faint ds-lands__label">{label}</p>
      <ul className="ds-lands__list">
        {rows.map((row, i) => (
          <li key={row.said} className="ds-lands__row" data-on={step >= 1 + i ? 'true' : undefined}>
            <span className="ds-lands__mark" aria-hidden="true">{row.mark}</span>
            <span className="ds-lands__said">{row.said}</span>
          </li>
        ))}
      </ul>
      {note && <p className="ds-small ds-lands__note">{note}</p>}
    </div>
  )
}

/**
 * A page reduced to what it actually says.
 *
 * The demonstration for the AI-readable files: a tall stack of lines
 * standing in for a real page, most of which is navigation, banners and a
 * footer, collapsing to the few lines that are the content. It is the whole
 * product in one move, and it needs no words on it.
 *
 * The rows that go are dimmed and scaled down rather than removed: removing
 * them would change the card's height, and a block that resizes mid-demo is
 * a scroll reveal by another name.
 */
export function Strip({
  before,
  after,
  keep,
  className,
}: {
  /** What the left column is. */
  before: string
  /** What the right column is. */
  after: string
  /** Which of the twelve lines survive, by index. */
  keep: number[]
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  const [stripped, setStripped] = useState(still)
  const started = useRef(still)

  const handOver = useCallback(() => {
    started.current = true
    setStripped(true)
  }, [])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    const timer = window.setTimeout(() => setStripped(true), 700)
    return () => window.clearTimeout(timer)
  }, [inView])

  /* Twelve lines of varying length: a page, drawn. Words here would be
     words about a business that does not exist. */
  const LINES = [78, 42, 96, 88, 64, 30, 92, 74, 38, 86, 56, 44]

  return (
    <div
      className={cn('ds-strip', className)}
      ref={ref}
      data-stripped={stripped ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <div className="ds-strip__side">
        <p className="ds-small ds-faint">{before}</p>
        <span className="ds-strip__page" aria-hidden="true">
          {LINES.map((width, i) => (
            <i key={i} style={{ width: `${width}%` }} data-keep={keep.includes(i) ? 'true' : undefined} />
          ))}
        </span>
      </div>
      <div className="ds-strip__arrow" aria-hidden="true">
        <ArrowRight />
      </div>
      <div className="ds-strip__side">
        <p className="ds-small ds-faint">{after}</p>
        <span className="ds-strip__page ds-strip__page--out" aria-hidden="true">
          {LINES.map((width, i) => (
            <i key={i} style={{ width: `${width}%` }} data-keep={keep.includes(i) ? 'true' : undefined} />
          ))}
        </span>
      </div>
    </div>
  )
}

/**
 * A page on one side, the facts read out of it on the other.
 *
 * The demonstration for schema generation: the point is not the JSON, it is
 * that a page becomes a set of named facts a machine can use. Nothing here
 * looks like code, which is a rule of this system and also the reason the
 * product is worth explaining this way at all.
 */
export function Read({
  label,
  facts,
  note,
  className,
}: {
  label: string
  /** Each fact: what it is, and what was read. */
  facts: { field: string; value: string }[]
  note?: string
  className?: string
}) {
  const [ref, inView] = useInView<HTMLDivElement>()
  const still = usePrefersReducedMotion()
  const last = facts.length + 1
  const [step, setStep] = useState(still ? last : 0)
  const started = useRef(still)
  const timers = useRef<number[]>([])

  const stop = useCallback(() => {
    timers.current.forEach(window.clearTimeout)
    timers.current = []
  }, [])

  const handOver = useCallback(() => {
    started.current = true
    stop()
    setStep(last)
  }, [stop, last])

  useEffect(() => {
    if (!inView || started.current) return
    started.current = true
    const at = [...facts.map((_, i) => 460 + i * 460), 460 + facts.length * 460 + 180]
    timers.current = at.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms))
    /*
      No cleanup on `inView`. This effect used to return `stop`, so React
      cleared the pending timers the moment the card left the viewport:
      scrolling past quickly froze it half-drawn, and `started` was already
      true so coming back never restarted it. It ran to completion only if
      you kept it on screen. Once begun it now finishes wherever the page
      is, and the unmount effect below is what clears the timers.
    */
  }, [inView, facts, stop])

  useEffect(() => stop, [stop])

  return (
    <div
      className={cn('ds-read', className)}
      ref={ref}
      data-done={step >= last ? 'true' : undefined}
      onPointerEnter={handOver}
      onFocusCapture={handOver}
    >
      <span className="ds-read__page" aria-hidden="true">
        <i style={{ width: '64%' }} />
        <i style={{ width: '92%' }} />
        <i style={{ width: '80%' }} />
        <i style={{ width: '44%' }} />
        <i style={{ width: '88%' }} />
        <i style={{ width: '58%' }} />
      </span>
      <div className="ds-read__out">
        <p className="ds-small ds-faint ds-read__label">{label}</p>
        <dl className="ds-read__facts">
          {facts.map((fact, i) => (
            <div key={fact.field} className="ds-read__fact" data-on={step >= 1 + i ? 'true' : undefined}>
              <dt>{fact.field}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
        {note && <p className="ds-small ds-read__note">{note}</p>}
      </div>
    </div>
  )
}
